import { Link } from 'react-router-dom';

type SessionType = 'opening' | 'session' | 'demo' | 'break' | 'panel' | 'closing';

interface ScheduleItem {
  time: string;
  duration: string;
  type: SessionType;
  title: string;
  subtitle?: string;
  speaker?: string;
  tools?: string[];
  outcome?: string;
  bullets?: string[];
}

const schedule: ScheduleItem[] = [
  {
    time: '0:00',
    duration: '10 mins',
    type: 'opening',
    title: 'opening & Strategic Context',
    speaker: 'Arch. Simbo Akinola-Odusola',
    bullets: ['Welcome & vision', 'The urgency of AI adoption', 'Positioning AI as a growth engine'],
    outcome: 'Set the stage for transformation',
  },
  {
    time: '0:10',
    duration: '15 mins',
    type: 'session',
    title: 'SESSION 1: AI for Daily Business Operations',
    speaker: 'Dr. Kayode Elusoji',
    bullets: ['AI as a virtual assistant', 'Automating core business tasks', 'Real SME use cases'],
    outcome: 'Immediate productivity gains',
  },
  {
    time: '0:25',
    duration: '15 mins',
    type: 'session',
    title: 'SESSION 2: AI Marketing, Sales & Customer Growth',
    speaker: 'GN. Olalekan Martins',
    bullets: ['AI-driven marketing strategies', 'Customer acquisition', 'Growth systems'],
    outcome: 'Increased revenue potential',
  },
  {
    time: '0:40',
    duration: '15 mins',
    type: 'session',
    title: 'SESSION 3: AI Strategy, Ecosystem & Future Readiness',
    speaker: 'Dr. Yakubu Kayode Saheed',
    bullets: ['AI ecosystem understanding', 'Positioning your business for the future', 'Strategic opportunities'],
    outcome: 'Long-term positioning',
  },
  {
    time: '0:55',
    duration: '5 mins',
    type: 'break',
    title: 'Break Session',
    bullets: ['Industry-based networking', 'AI challenges discussion'],
  },
  {
    time: '1:00',
    duration: '35 mins',
    type: 'demo',
    title: 'LIVE DEMO: Run Your Business with AI in Real-Time',
    bullets: ['Content generation', 'Marketing design', 'Customer automation'],
    outcome: 'Hands-on understanding of AI tools',
  },
  {
    time: '1:35',
    duration: '20 mins',
    type: 'panel',
    title: 'Question & Answer Session',
    subtitle: 'All speakers participate',
    outcome: 'Real problems solved live',
  },
  {
    time: '1:55',
    duration: '5 mins',
    type: 'closing',
    title: 'Closing & Call to Action',
    speaker: 'Dr. Kayode Elusoji',
    bullets: ['Implementation roadmap', 'Next steps'],
    outcome: '"Start small. Start now. Scale with AI."',
  },
];

const typeConfig: Record<SessionType, { bg: string; badge: string; badgeText: string; icon: string }> = {
  opening: { bg: 'bg-white border border-gray-200', badge: 'bg-gray-100 text-gray-600', badgeText: 'OPENING', icon: 'ri-flag-line' },
  session: { bg: 'bg-white border border-gray-200', badge: 'bg-green-100 text-green-700', badgeText: 'SESSION', icon: 'ri-mic-line' },
  demo: { bg: 'bg-green-50 border border-green-200', badge: 'bg-green-700 text-white', badgeText: 'LIVE DEMO', icon: 'ri-settings-3-line' },
  break: { bg: 'bg-gray-50 border border-gray-200', badge: 'bg-gray-200 text-gray-600', badgeText: 'BREAK', icon: 'ri-cup-line' },
  panel: { bg: 'bg-amber-50 border border-amber-200', badge: 'bg-amber-500 text-white', badgeText: 'PANEL Q&A', icon: 'ri-question-answer-line' },
  closing: { bg: 'bg-white border border-gray-200', badge: 'bg-gray-800 text-white', badgeText: 'CLOSING', icon: 'ri-flag-2-line' },
};

const ScheduleSection = () => {
  return (
    <section id="schedule" className="py-20 md:py-28 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold uppercase tracking-widest mb-4">
            Program Schedule
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
            2 Hours of Pure
            <span className="bg-gradient-to-r from-green-700 to-green-600 bg-clip-text text-transparent"> Business Transformation</span>
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            4 Speakers · 1 Live Demonstration · Deep Engagement · Real Outcomes · May 30, 2026
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[52px] md:left-[60px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-600 via-amber-400 to-green-500"></div>

          <div className="space-y-6">
            {schedule.map((item, idx) => {
              const cfg = typeConfig[item.type];
              return (
                <div key={idx} className="flex gap-4 md:gap-6 items-start">
                  {/* Time Badge */}
                  <div className="shrink-0 flex flex-col items-center z-10">
                    <div className="w-[52px] md:w-[60px] h-[52px] md:h-[60px] flex flex-col items-center justify-center rounded-full bg-white border-2 border-green-600">
                      <span className="text-green-700 font-bold text-[10px] leading-none text-center">{item.time}</span>
                      <span className="text-gray-400 text-[9px] mt-0.5">{item.duration}</span>
                    </div>
                  </div>

                  {/* Card */}
                  <div className={`flex-1 rounded-2xl p-5 ${cfg.bg} hover:-translate-y-0.5 transition-transform duration-200`}>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.badge}`}>
                        {cfg.badgeText}
                      </span>
                      {item.speaker && (
                        <span className="text-gray-500 text-xs flex items-center gap-1">
                          <i className="ri-user-line text-xs"></i>
                          {item.speaker}
                        </span>
                      )}
                    </div>

                    <h4 className="font-bold text-gray-900 text-base mb-2">{item.title}</h4>

                    {item.subtitle && (
                      <p className="text-gray-500 text-sm mb-2 italic">{item.subtitle}</p>
                    )}

                    {item.tools && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {item.tools.map((tool) => (
                          <span key={tool} className="px-2.5 py-1 rounded-lg bg-white border border-green-200 text-green-700 text-xs font-semibold">
                            {tool}
                          </span>
                        ))}
                      </div>
                    )}

                    {item.bullets && (
                      <ul className="space-y-1 mb-3">
                        {item.bullets.map((b) => (
                          <li key={b} className="flex items-center gap-2 text-gray-600 text-sm">
                            <i className="ri-arrow-right-s-line text-green-600 shrink-0"></i>
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}

                    {item.outcome && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200/60">
                        <i className="ri-trophy-line text-amber-500 text-sm shrink-0"></i>
                        <span className="text-green-700 text-xs font-semibold italic">{item.outcome}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* View Full Schedule Link */}
        <div className="mt-10 text-center">
          <Link
            to="/schedule"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-green-600 text-green-700 font-semibold text-sm hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-line"></i>
            View Full Schedule Page
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ScheduleSection;
